import { useState } from 'react';
import styled from 'styled-components';
import { createUrlParameters } from '@comeon/utility';
import { motion, AnimatePresence } from 'framer-motion';

import { requestApi } from '~/services/api.js';
import Loading from '~/components/misc/loading.js';
import { getZindexByType } from '~/utils/style-utils.ts';
import useTranslations from '~/components/translation/use-translations.ts';
import TermsAndConditions from '~/components/pages/terms-and-conditions.js';
import PrivacyPolicy from '~/components/pages/privacy-policy-configured.js';
import BinaryQuestion from '~/components/binary-question';
import { ScenarioFlow } from '~/components/account/limits/constants.ts';

const springSettings = { type: 'spring', stiffness: 300, damping: 200 };
const variants = {
	enter: {
		transform: `translate3d(0%, 0%, 0)`,
		height: '100%',
		opacity: 1
	},
	exit: { transform: `translate3d(0%, 100%, 0)`, opacity: 0 }
};

const ModalContainer = styled(motion.div)`
	height: 100%;
	position: absolute;
	width: 100%;
	left: 0;
	top: 0;
	z-index: ${getZindexByType('blocking-modal-view')};
`;

const StyledSpan = styled.span`
	a {
		text-decoration: underline;
	}
`;

const textKeys = ['SCHUFA_CONSENT_HEADER', 'SCHUFA_CONSENT_INFO', 'APPLEPAY_CONFIRM_TEXT', 'CANCEL', 'CONTINUE'];

const SchufaConsentPopup = ({ amount, status, onSuccess, onReject, setSchufaError, qdDepositFlow = false }) => {
	const [modal, updateModal] = useState(null);
	const [successConfirm, showConfirm] = useState(false);
	const { sum_min: sumMin = '', methodId } = status || {};
	const hasSelectedApplePay = parseInt(methodId, 10) === 62;
	const { translations: t, isLoadingTranslations } = useTranslations(textKeys);
	const scenarioFlow = qdDepositFlow ? ScenarioFlow.QD_DEPOSIT_FLOW : ScenarioFlow.DEPOSIT_FLOW;

	const onYesClick = async () => {
		const onCancel = type => {
			onReject();
			return setSchufaError(type);
		};
		try {
			const response = await requestApi(
				`/PaymentFormDataService/getDepositFeesAndLimits/forSchufaCreditCheck`,
				createUrlParameters({ amount, scenarioFlow }),
				'POST',
				true
			);
			const { success, remainingAmount } = response || {};
			if (success !== true) {
				onCancel('schufaNetworkError');
			} else if (remainingAmount !== '' && Number(remainingAmount) < Number(sumMin)) {
				/** here we check partial deposit and 0 remaining with schufa cases, we get remainingAmount as a empty string for success case */
				onCancel('schufaLimitError');
			} else {
				updateModal(null);
				// eslint-disable-next-line no-unused-expressions
				hasSelectedApplePay ? showConfirm(true) : onSuccess();
			}
		} catch (err) {
			// throw new Error(err.message);
			onCancel('schufaNetworkError');
		}
	};

	const renderModal = () => {
		switch (true) {
			case modal === 'terms':
				return <TermsAndConditions hideBackButton={false} onCloseCB={() => updateModal(null)} />;
			case modal === 'privacy':
				return <PrivacyPolicy hideBackButton={false} onClose={() => updateModal(null)} />;

			default:
				updateModal(null);
				return null;
		}
	};

	const changeModal = e => {
		e.preventDefault();
		const { id } = e.target?.dataset || {}; // id - received from the textkey SCHUFA_CONSENT_INFO. Possible values for now 'privacy' and 'terms'
		if (id) {
			updateModal(id);
		}
	};

	const onSuccessConfirm = () => {
		showConfirm(false);
		onSuccess();
	};

	return (
		<div data-at="schufa-consent-popup">
			{successConfirm ? (
				<BinaryQuestion
					shouldOpenBinary
					renderInParent
					text={t.APPLEPAY_CONFIRM_TEXT}
					yesText={t.CONTINUE}
					onYesClick={onSuccessConfirm}
				/>
			) : (
				<BinaryQuestion
					shouldOpenBinary
					renderInParent={!qdDepositFlow}
					buttonsHasUncontrolledLoading
					title={t.SCHUFA_CONSENT_HEADER}
					text={
						<StyledSpan
							onClick={changeModal}
							onKeyDown={changeModal}
							role="button"
							tabIndex={0}
							aria-label="privacy policy"
							dangerouslySetInnerHTML={{
								__html: t.SCHUFA_CONSENT_INFO
							}}
						/>
					}
					yesText={t.CONTINUE}
					onYesClick={onYesClick}
					noText={t.CANCEL}
					onNoClick={onReject}
					onOutsideClick={onReject}
				/>
			)}
			{modal && (
				<AnimatePresence>
					<ModalContainer
						variants={variants}
						animate="enter"
						initial="exit"
						exit="exit"
						key="slideUp"
						transition={{
							transform: { springSettings },
							opacity: { springSettings }
						}}
					>
						{renderModal()}
					</ModalContainer>
				</AnimatePresence>
			)}
			{isLoadingTranslations ? <Loading /> : null}
		</div>
	);
};

export default SchufaConsentPopup;
